import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { COLORS } from '../../../../../configs/colors';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';
import { HistoryTableProps } from './HistoryNotesPanel.props';
import { SafeNotes, InteractionDetails, groupInteractionsWithTrigger } from './HistoryLogRenderers';

/** Log type codes that represent reported/creation notes — shown in the Reported Information panel instead. */
const CREATION_NOTE_CODES = new Set(['creation-notes', 'creation_notes', 'reported-notes']);

const cellSx = { fontSize: '12px', py: 0.75, px: 1.5 };
const headerSx = {
  ...cellSx,
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

const NOTE_C = { bg: COLORS.HistoryNoteBg, text: COLORS.HistoryNoteText };
const ATT_C = { bg: COLORS.HistoryAttendanceBg, text: COLORS.HistoryAttendanceText };
const STS_C = { bg: COLORS.HistoryStatusBg, text: COLORS.HistoryStatusText };
const SYS_C = { bg: COLORS.HistorySystemBg, text: COLORS.HistorySystemText };
const typeColors: Record<string, { bg: string; text: string }> = {
  notes: NOTE_C, note: NOTE_C, email: ATT_C, 'status-change': STS_C, status: STS_C,
  system: SYS_C, attendance: ATT_C, 'check-in': ATT_C, 'check-out': ATT_C,
  allocation: { bg: COLORS.HistoryAssignmentBg, text: COLORS.HistoryAssignmentText },
  'part-action': { bg: COLORS.HistoryPartsBg, text: COLORS.HistoryPartsText },
};

const getTypeChipColors = (typeCode: string): { bg: string; text: string } => {
  const lower = String(typeCode).toLowerCase();
  return typeColors[lower] ?? { bg: COLORS.HistorySystemBg, text: COLORS.HistorySystemText };
};

const formatDateTime = (dateStr: unknown): string => {
  if (!dateStr || typeof dateStr !== 'string') return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  } catch {
    return '-';
  }
};

/** Map filter keys to their matching logTypeCodes. */
const FILTER_CODE_MAP: Record<string, string[]> = {
  notes: ['notes'],
  status: ['status-change', 'status'],
  parts: ['part-actions-install-serialised', 'part-actions-remove-serialised', 'part-actions-replace-serialised', 'part-actions-doa-serialised', 'part-actions-clean-serialised', 'part-actions-test-serialised', 'part-actions-repair-serialised', 'part-actions-adjust-serialised'],
  assignment: ['assignment-events-primary', 'assignment-events-secondary', 'allocation'],
  attendance: ['check-in', 'check-out', 'attendance'],
  uploads: ['uploads'],
  interactions: ['interaction-events'],
};

const isNoteLog = (code: string) => code === 'notes' || code === 'note';

export const HistoryTable: React.FC<HistoryTableProps> = ({
  logs,
  filterType,
  sortDirection,
  onEditNote,
  onDeleteNote,
  canEditNotes = false,
  canDeleteNotes = false,
}) => {
  const showActions = canEditNotes || canDeleteNotes;
  // Exclude creation/reported notes — those are shown in the Reported Information panel
  const nonCreationLogs = logs.filter(
    (log) => !CREATION_NOTE_CODES.has(String(log.logTypeCode ?? '').toLowerCase()),
  );

  const typeFiltered =
    filterType === 'all'
      ? nonCreationLogs
      : nonCreationLogs.filter((log) => {
          const code = String(log.logTypeCode ?? log.type ?? '').toLowerCase();
          const codes = FILTER_CODE_MAP[filterType];
          if (codes) return codes.some((c) => code.includes(c));
          return code === filterType.toLowerCase();
        });

  const sorted = [...typeFiltered].sort((a, b) => {
    const timeA = new Date(String(a.time ?? a.createdAt ?? '')).getTime() || 0;
    const timeB = new Date(String(b.time ?? b.createdAt ?? '')).getTime() || 0;
    return sortDirection === 'newest' ? timeB - timeA : timeA - timeB;
  });

  const filteredLogs = groupInteractionsWithTrigger(sorted);

  // Build from-status map: for each status-change log, find the previous status
  // by walking all logs in chronological order
  const fromStatusMap = new Map<string | number, string>();
  const chronological = [...nonCreationLogs].sort((a, b) => {
    const timeA = new Date(String(a.time ?? a.createdAt ?? '')).getTime() || 0;
    const timeB = new Date(String(b.time ?? b.createdAt ?? '')).getTime() || 0;
    return timeA - timeB;
  });
  let prevStatus = '';
  for (const log of chronological) {
    const code = String(log.logTypeCode ?? '').toLowerCase();
    const currentStatus = String(log.serviceStatusName ?? '');
    if (code === 'status-change' || code === 'status') {
      fromStatusMap.set(log.id as string | number, prevStatus);
    }
    if (currentStatus) prevStatus = currentStatus;
  }

  const isStatusChange = (code: string) =>
    code === 'status-change' || code === 'status';

  if (filteredLogs.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: '13px',
          color: 'text.secondary',
          py: 2,
          textAlign: 'center',
        }}
      >
        No history entries
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, width: 120 }}>Time</TableCell>
            <TableCell sx={{ ...headerSx, width: 100 }}>User</TableCell>
            <TableCell sx={{ ...headerSx, width: 80 }}>Status</TableCell>
            <TableCell sx={{ ...headerSx, width: 90 }}>Action</TableCell>
            <TableCell sx={headerSx}>Notes</TableCell>
            {showActions && <TableCell sx={{ ...headerSx, width: 70 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredLogs.map((log, idx) => {
            const typeCode = String(log.logTypeCode ?? log.type ?? '-');
            const typeName = String(log.logTypeName ?? typeCode);
            const chipColors = getTypeChipColors(typeCode);
            const notes = String(log.notes ?? '-');

            return (
              <TableRow
                key={String(log.id ?? idx)}
                sx={{
                  bgcolor: idx % 2 === 0 ? 'transparent' : 'v4.tableEven',
                  '&:hover': { bgcolor: 'v4.tableHover' },
                }}
              >
                <TableCell sx={{ ...cellSx, width: 120, whiteSpace: 'nowrap' }}>
                  {formatDateTime(log.time ?? log.createdAt ?? log.dateTime)}
                </TableCell>
                <TableCell sx={{ ...cellSx, width: 100 }}>
                  {String(log.userName ?? log.user ?? log.createdBy ?? '-')}
                </TableCell>
                <TableCell sx={{ ...cellSx, width: 80 }}>
                  {String(log.serviceStatusName ?? log.status ?? '-')}
                </TableCell>
                <TableCell sx={{ ...cellSx, width: 90 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      px: 1,
                      py: 0.25,
                      borderRadius: 2,
                      fontSize: '10px',
                      fontWeight: 600,
                      bgcolor: chipColors.bg,
                      color: chipColors.text,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {typeName}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {isStatusChange(typeCode) && (() => {
                    const fromStatus = fromStatusMap.get(log.id as string | number) ?? '';
                    const toStatus = String(log.serviceStatusName ?? '');
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: notes && notes !== '-' ? 0.75 : 0 }}>
                        {fromStatus ? <StatusBadge status={fromStatus} size="small" /> : <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>—</Typography>}
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>→</Typography>
                        <StatusBadge status={toStatus} size="small" />
                      </Box>
                    );
                  })()}
                  <SafeNotes html={notes} />
                  <InteractionDetails data={log.data} />
                  {(log._attachedInteractions as Array<Record<string, unknown>> | undefined)?.map((iLog, iIdx) => (
                    <Box key={iIdx} sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'v4.accent', py: 0.5 }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'v4.accent', mb: 0.25 }}>
                        Interaction
                      </Typography>
                      <SafeNotes html={String(iLog.notes ?? '')} />
                      <InteractionDetails data={iLog.data} />
                    </Box>
                  ))}
                </TableCell>
                {showActions && (
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity 0.15s', 'tr:hover &': { opacity: 1 } }}>
                      {canEditNotes && isNoteLog(typeCode) && (
                        <Tooltip title="Edit note">
                          <IconButton size="small" aria-label="edit note" onClick={() => onEditNote?.(log.id as number)}>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteNotes && (
                        <Tooltip title="Hide record">
                          <IconButton size="small" aria-label="hide record" onClick={() => onDeleteNote?.(log.id as number)}>
                            <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
